export type SPAConfigPrimitive = string | number | boolean | RegExp;
export type SPAConfigArray = ReadonlyArray<SPAConfigPrimitive>;
export type SPAConfigValue = SPAConfigPrimitive | SPAConfigArray;

export default class SPAConfig<T extends { [key: string]: SPAConfigValue }> {
  private readonly _defaults: T;
  private readonly _environments = new Map<string, T>();
  private _active: T;

  constructor(defaults: T, environments: { [hostname: string]: Partial<T> } = {}, hostname = window.location.hostname) {
    this._defaults = this.copyConfig(defaults);

    Object.getOwnPropertyNames(environments).forEach((environment) => {
      this._environments.set(environment, this.extendConfig(environments[environment]));
    });

    this._active = this._environments.get(hostname) || this._defaults;
  }

  public get<K extends keyof T>(key: K): T[K] extends SPAConfigArray ? ReadonlyArray<T[K]> : T[K] {
    return this._active[key] as T[K] extends SPAConfigArray ? ReadonlyArray<T[K]> : T[K];
  }

  public toObject(): T {
    return this.copyConfig(this._active);
  }

  private copyConfig<U extends { [key: string]: SPAConfigValue }>(config: U): U {
    const copy = { ...config };

    Object.getOwnPropertyNames(copy).forEach((_key) => {
      const key: keyof U = _key;
      const value = copy[key];
      if (value instanceof Array) copy[key] = value.slice() as any;
    });

    return copy;
  }

  private extendConfig(config: Partial<T>): T {
    const extended = this.copyConfig(this._defaults);

    Object.getOwnPropertyNames(config).forEach((_key) => {
      const key: keyof T = _key;
      const value = config[key];
      if (value instanceof Array) extended[key] = value.slice() as any;
      else if (value !== undefined) extended[key] = value as any;
    });

    return extended;
  }
}
